import type { AccountView, Book, BookRole, BookStatus } from '../types'

/**
 * Derives a display role for an account. Pure, never throws.
 *
 * Precedence:
 *   1. broker_mode === 'alpaca_live'                -> 'live'
 *   2. non-live AND pick_source_account_id != null   -> 'validation'
 *   3. is_active_pointer                             -> 'research'
 *   4. otherwise: 'research' if this is the only known account,
 *      else 'book' (generic, for any additional accounts we don't
 *      otherwise recognize).
 */
export function deriveBookRole(acct: AccountView, all: AccountView[]): BookRole {
  if (acct.broker_mode === 'alpaca_live') return 'live'
  if (acct.pick_source_account_id != null) return 'validation'
  if (acct.is_active_pointer) return 'research'
  return all.length <= 1 ? 'research' : 'book'
}

/**
 * Joins each account to its (optional) BookStatus from engine/status's
 * books[] array — matched on account_id — and attaches the derived role.
 * A disabled account legitimately has no matching BookStatus.
 */
export function deriveBooks(accounts: AccountView[], books: BookStatus[]): Book[] {
  return accounts.map((acct) => {
    const bookStatus = books.find((b) => b.account_id === acct.id) ?? null
    return {
      ...acct,
      bookStatus,
      role: deriveBookRole(acct, accounts),
    }
  })
}
